import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSEO } from "../hooks/useSEO";
import { useReveal } from "../hooks/useReveal";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatDateShort(iso) {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

function BodyText({ text }) {
  if (!text) return null;
  const paragraphs = text.split(/\n{2,}/).filter(Boolean);

  return (
    <div>
      {paragraphs.map((para, i) => {
        if (i === 0) {
          const firstLetter = para.charAt(0);
          const rest = para.slice(1);
          return (
            <p key={i} style={{ color: "rgba(248,246,240,0.88)", fontSize: 19, lineHeight: 1.88, marginBottom: 36 }}>
              <span
                className="font-serif"
                style={{
                  float: "left",
                  fontSize: "clamp(80px, 10vw, 120px)",
                  lineHeight: 0.82,
                  paddingRight: 16,
                  marginTop: 8,
                  color: "#C9A961",
                  fontStyle: "italic",
                }}
                aria-hidden="true"
              >
                {firstLetter}
              </span>
              {rest}
            </p>
          );
        }
        if (para.startsWith("— ") || para.startsWith("« ")) {
          return (
            <blockquote
              key={i}
              className="font-serif"
              style={{
                fontSize: "clamp(22px, 2.5vw, 32px)",
                lineHeight: 1.35,
                color: "#F8F6F0",
                fontStyle: "italic",
                borderLeft: "2px solid #C9A961",
                paddingLeft: 32,
                margin: "56px 0",
              }}
            >
              {para}
            </blockquote>
          );
        }
        return (
          <p
            key={i}
            style={{ color: "rgba(248,246,240,0.75)", fontSize: 18, lineHeight: 1.88, marginBottom: 28 }}
          >
            {para}
          </p>
        );
      })}
    </div>
  );
}

export default function PressArticle() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useSEO(
    article ? article.title : "Communiqué",
    article ? article.excerpt : "Salle de presse — Le Stand d'Or",
  );

  const bodyRef = useReveal();
  const metaRef = useReveal();

  useEffect(() => {
    api.get(`/press/${id}`)
      .then(({ data }) => setArticle(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="pt-[160px] pb-40 max-w-[900px] mx-auto px-6 lg:px-12">
        <p className="label" style={{ color: "#A1A1AA" }}>Chargement…</p>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="pt-[160px] pb-40 max-w-[900px] mx-auto px-6 lg:px-12">
        <p className="label mb-6" style={{ color: "#A1A1AA" }}>Article introuvable</p>
        <Link to="/presse" className="label" style={{ color: "#C9A961" }}>← Retour à la salle de presse</Link>
      </div>
    );
  }

  return (
    <div data-testid="press-article-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden grain"
        style={{ background: "#000", minHeight: "70vh", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}
        data-testid="press-article-hero"
      >
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.6) 60%, #000 100%)" }}
          aria-hidden="true"
        />

        {article.image_url && (
          <div
            className="absolute inset-0 bg-center bg-cover"
            style={{
              backgroundImage: `url(${article.image_url})`,
              filter: "grayscale(100%) brightness(0.28)",
            }}
            aria-hidden="true"
          />
        )}

        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12 pb-20 pt-[180px] w-full">
          <Link
            to="/presse"
            className="label manifeste-fade"
            style={{ color: "#A1A1AA", display: "inline-block", marginBottom: 48 }}
          >
            ← Salle de presse
          </Link>

          <p className="label manifeste-fade" style={{ color: "#C9A961", marginBottom: 24 }}>
            Communiqué officiel — {formatDateShort(article.date)}
          </p>

          <h1
            className="font-serif manifeste-title"
            style={{
              fontSize: "clamp(44px, 8vw, 130px)",
              lineHeight: 0.92,
              letterSpacing: "-0.03em",
              color: "#F8F6F0",
              maxWidth: 1000,
            }}
            data-testid="press-article-title"
          >
            {article.title}
          </h1>
        </div>
      </section>

      {/* META BAR */}
      <div
        ref={metaRef}
        className="reveal"
        style={{ borderBottom: "1px solid #1c1c1c", background: "#0A0A0A" }}
      >
        <div
          className="max-w-[1200px] mx-auto px-6 lg:px-12 py-8 flex flex-wrap items-center gap-10"
        >
          <div>
            <p className="label" style={{ color: "#A1A1AA", marginBottom: 4 }}>Date</p>
            <p className="font-serif" style={{ color: "#F8F6F0", fontSize: 18 }}>{formatDate(article.date)}</p>
          </div>
          <div style={{ width: 1, height: 40, background: "#1c1c1c", flexShrink: 0 }} />
          <div>
            <p className="label" style={{ color: "#A1A1AA", marginBottom: 4 }}>Source</p>
            <p className="font-serif" style={{ color: "#F8F6F0", fontSize: 18 }}>Le Stand d'Or</p>
          </div>
          <div style={{ width: 1, height: 40, background: "#1c1c1c", flexShrink: 0 }} />
          <div>
            <p className="label" style={{ color: "#A1A1AA", marginBottom: 4 }}>Contact presse</p>
            <a
              href="mailto:presse@lestanddor.com"
              className="font-serif"
              style={{ color: "#C9A961", fontSize: 18, borderBottom: "1px solid rgba(201,169,97,0.4)", paddingBottom: 1 }}
            >
              presse@lestanddor.com
            </a>
          </div>
        </div>
      </div>

      {/* EXCERPT / CHAPÔ */}
      {article.excerpt && (
        <div style={{ borderBottom: "1px solid #1c1c1c", background: "#050505" }}>
          <div className="max-w-[900px] mx-auto px-6 lg:px-12 py-20">
            <p
              className="font-serif"
              style={{
                fontSize: "clamp(22px, 3vw, 38px)",
                lineHeight: 1.35,
                color: "rgba(248,246,240,0.85)",
                fontStyle: "italic",
                letterSpacing: "-0.01em",
              }}
              data-testid="press-article-excerpt"
            >
              {article.excerpt}
            </p>
          </div>
        </div>
      )}

      {/* BODY */}
      <section
        ref={bodyRef}
        className="reveal max-w-[780px] mx-auto px-6 lg:px-12 py-24 md:py-36"
        data-testid="press-article-body"
      >
        <BodyText text={article.body} />
      </section>

      {/* FOOTER NAV */}
      <div
        className="max-w-[1200px] mx-auto px-6 lg:px-12 py-20 flex items-center justify-between flex-wrap gap-8"
        style={{ borderTop: "1px solid #1c1c1c" }}
      >
        <Link
          to="/presse"
          className="label"
          style={{ color: "#C9A961", borderBottom: "1px solid rgba(201,169,97,0.4)", paddingBottom: 3 }}
        >
          ← Tous les communiqués
        </Link>
        <p className="label" style={{ color: "rgba(248,246,240,0.3)", letterSpacing: "0.12em" }}>
          Le Stand d'Or — {new Date(article.date).getFullYear()}
        </p>
      </div>
    </div>
  );
}
